/**
 * Role-based permission helpers for community UI.
 * Mirrors backend rules in communityModerationService.ts and communityService.ts.
 */

export type CommunityRole = 'owner' | 'moderator' | 'member';
export type CommunityVisibility = 'public' | 'private' | 'invite_only';

export interface CommunityInviteLike {
  use_count?: number;
  max_uses?: number | null;
  revoked_at?: string | null;
}

export function isInviteListed(invite: CommunityInviteLike): boolean {
  if (!invite.revoked_at) return true;
  return (invite.use_count ?? 0) > 0;
}

export function isInviteConsumed(invite: CommunityInviteLike): boolean {
  if (invite.revoked_at) return true;
  if (invite.max_uses === 1 && (invite.use_count ?? 0) >= 1) return true;
  if (
    typeof invite.max_uses === 'number' &&
    invite.max_uses > 0 &&
    (invite.use_count ?? 0) >= invite.max_uses
  ) {
    return true;
  }
  return false;
}

export interface CommunitySettings {
  allow_member_group_create?: boolean;
  allow_member_invite?: boolean;
  default_post_policy?: 'all_members' | 'admins_only';
  profanity_filter?: boolean;
}

const ROLE_RANK: Record<CommunityRole, number> = {
  owner: 3,
  moderator: 2,
  member: 1,
};

export function hasRoleAtLeast(
  role: CommunityRole | undefined,
  min: CommunityRole
): boolean {
  if (!role) return false;
  return ROLE_RANK[role] >= ROLE_RANK[min];
}

export function canViewMembers(role: CommunityRole | undefined): boolean {
  return hasRoleAtLeast(role, 'member');
}

export function canEditCommunity(role: CommunityRole | undefined): boolean {
  return hasRoleAtLeast(role, 'moderator');
}

export function canChangeRole(role: CommunityRole | undefined): boolean {
  return role === 'owner';
}

export function canManageInvites(role: CommunityRole | undefined): boolean {
  return role === 'owner';
}

/** Private communities use single-use invite links; public links never expire by use count. */
export function isSingleUseInviteCommunity(visibility: CommunityVisibility): boolean {
  return visibility === 'private' || visibility === 'invite_only';
}

export function inviteLinkCopiedMessage(visibility: CommunityVisibility): string {
  if (isSingleUseInviteCommunity(visibility)) {
    return 'Single-use invite link copied. Share a new link for each person you invite.';
  }
  return 'Invite link copied to clipboard';
}

export function canShareInvite(
  role: CommunityRole | undefined,
  visibility: CommunityVisibility,
  settings?: CommunitySettings
): boolean {
  if (!role) return false;
  if (visibility === 'private' || visibility === 'invite_only') {
    return hasRoleAtLeast(role, 'moderator');
  }
  if (hasRoleAtLeast(role, 'moderator')) return true;
  return settings?.allow_member_invite !== false;
}

export function canDeleteCommunity(role: CommunityRole | undefined): boolean {
  return role === 'owner';
}

export function canTransferOwnership(role: CommunityRole | undefined): boolean {
  return role === 'owner';
}

export function isOnlyCommunityMember(memberCount: number): boolean {
  return memberCount <= 1;
}

/** Owner with other active members must transfer before leaving. */
export function mustTransferBeforeLeave(
  role: CommunityRole | undefined,
  memberCount: number
): boolean {
  return role === 'owner' && memberCount > 1;
}

export function canRemoveMember(
  actorRole: CommunityRole | undefined,
  targetRole: CommunityRole,
  actorClientId: string,
  targetClientId: string
): boolean {
  if (!actorRole || actorClientId === targetClientId) return false;
  if (targetRole === 'owner') return false;
  if (actorRole === 'owner') return true;
  if (actorRole === 'moderator' && targetRole === 'member') return true;
  return false;
}

export function roleLabel(role: CommunityRole): string {
  switch (role) {
    case 'owner':
      return 'Owner';
    case 'moderator':
      return 'Admin';
    default:
      return 'Member';
  }
}

export function visibilityLabel(visibility: CommunityVisibility): string {
  switch (visibility) {
    case 'public':
      return 'Public';
    case 'private':
      return 'Private';
    case 'invite_only':
      return 'Invite only';
  }
}
